export const API_ERROR_CODE = {
  UNAUTHORIZED: "unauthorized",
  CONFLICT: "conflict",
  GAME_LIMIT_REACHED: "game_limit_reached",
  GAME_ENTRY_LIMIT_REACHED: "game_entry_limit_reached",
} as const;

export const API_ERROR_HTTP_STATUS = {
  UNAUTHORIZED: 401,
  CONFLICT: 409,
} as const;

export const QUOTA_API_ERROR_CODE = {
  GAME_LIMIT_REACHED: API_ERROR_CODE.GAME_LIMIT_REACHED,
  GAME_ENTRY_LIMIT_REACHED: API_ERROR_CODE.GAME_ENTRY_LIMIT_REACHED,
} as const;

export type QuotaApiErrorCode = (typeof QUOTA_API_ERROR_CODE)[keyof typeof QUOTA_API_ERROR_CODE];

interface ApiErrorParams {
  status: number;
  message: string;
  code?: string | null;
  details?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly details: unknown;

  constructor({ status, message, code = null, details }: ApiErrorParams) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function getStringProperty(value: Record<string, unknown>, property: string): string | null {
  const propertyValue = value[property];
  return typeof propertyValue === "string" ? propertyValue : null;
}

function stringifyApiErrorBody(body: unknown): string | null {
  if (body === null || body === undefined) {
    return null;
  }

  if (typeof body === "string") {
    return body;
  }

  try {
    return JSON.stringify(body);
  } catch {
    return null;
  }
}

async function readApiErrorBody(response: Response): Promise<unknown> {
  const text = (await response.text()).trim();
  if (!text) {
    return null;
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json") || text.startsWith("{") || text.startsWith("[")) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }

  return text;
}

export async function parseApiErrorResponse(response: Response): Promise<ApiError> {
  const body = await readApiErrorBody(response);
  const fallbackMessage = response.statusText || `Request failed (${response.status})`;

  if (isRecord(body)) {
    const message =
      getStringProperty(body, "message") ?? getStringProperty(body, "error") ?? stringifyApiErrorBody(body);

    return new ApiError({
      status: response.status,
      code: getStringProperty(body, "code"),
      message: message ?? fallbackMessage,
      details: body.details,
    });
  }

  return new ApiError({
    status: response.status,
    message: stringifyApiErrorBody(body) ?? fallbackMessage,
  });
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isUnauthenticatedApiError(error: unknown): boolean {
  return (
    isApiError(error) &&
    (error.status === API_ERROR_HTTP_STATUS.UNAUTHORIZED || error.code === API_ERROR_CODE.UNAUTHORIZED)
  );
}

export function isQuotaApiErrorCode(code: string | null): code is QuotaApiErrorCode {
  return (
    code === QUOTA_API_ERROR_CODE.GAME_LIMIT_REACHED ||
    code === QUOTA_API_ERROR_CODE.GAME_ENTRY_LIMIT_REACHED
  );
}

export function isQuotaApiError(error: unknown): error is ApiError & { code: QuotaApiErrorCode } {
  return isApiError(error) && isQuotaApiErrorCode(error.code);
}

export function getQuotaApiErrorMessage(error: unknown): string | null {
  return isQuotaApiError(error) ? error.message : null;
}

export function getGameEntryMutationApiErrorMessage(error: unknown): string | null {
  if (!isApiError(error)) {
    return null;
  }

  if (error.code === API_ERROR_CODE.GAME_ENTRY_LIMIT_REACHED) {
    return error.message;
  }

  if (error.code === API_ERROR_CODE.CONFLICT || error.status === API_ERROR_HTTP_STATUS.CONFLICT) {
    return error.message;
  }

  return null;
}
